/**
 * Phase 49, Accept-invite landing for Supabase admin-invite (private beta).
 *
 * Distinct from /invite (workspace-share invites). This page handles the
 * Supabase Auth admin-invite flow:
 *
 *  1. Supabase redirects the invited user here with auth tokens in the URL
 *     fragment (#access_token=...&refresh_token=...&type=invite). The
 *     `supabase-js` client auto-detects the fragment on init, exchanges
 *     the tokens for a session, and stores it. We just wait for the
 *     session to appear.
 *  2. With a session in hand, show "Set your password", the invite type
 *     pre-confirms the email but does NOT set a password.
 *  3. On submit: `supabase.auth.updateUser({ password })`, then call our
 *     existing `/api/auth/welcome` endpoint to backfill names from the
 *     waitlist row and queue the delayed expectations email.
 *  4. Navigate to /onboarding.
 *
 * Failure modes:
 *  - Token expired (24h) → no session arrives → calm explainer + CTA to ask
 *    for a fresh invite (mailto accounts@2120.money).
 *  - User lands without any token (e.g. opened /accept-invite directly) →
 *    same calm explainer.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, Sparkles, Shield, ShieldAlert, Loader2, Users2 } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import usePageTitle from '@/hooks/usePageTitle';

const SESSION_WAIT_MS = 5000; // generous, covers slow Supabase token exchange

export default function AcceptBetaInvite() {
  usePageTitle('Accept your invitation');
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  // Phase 84.4 — Family-invite fast-path.
  // Workspace-share invites now embed a `ws_token` in the redirect_to URL.
  // When present, we know this invitee is joining a partner's workspace,
  // so after setting their password we auto-redeem the workspace invite
  // and land them on /dashboard instead of the full /onboarding flow.
  const wsToken = (searchParams.get('ws_token') || '').trim();
  const [inviterName, setInviterName] = useState('');

  // 'pending' = SDK still parsing URL, 'ready' = session present,
  // 'expired' = no session arrived within timeout.
  const [phase, setPhase] = useState('pending');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  // Phase 84.20.8 — Collect first & last name at invite-accept time.
  // Historically we backfilled these from the waitlist row via
  // /api/auth/welcome, but many waitlist rows had blank names (the
  // field was optional) — which left profiles.name empty for that
  // subset of users. Making the two fields mandatory here guarantees
  // that every user has a properly greetable first & last name from
  // the moment they land on the dashboard.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [busy, setBusy] = useState(false);

  // Phase 55 follow-up — self-serve resend of an expired/used invite.
  // Lives only on the expired card. Backend always returns 200 with a
  // generic message so we don't leak waitlist membership; we mirror that
  // in the UI by showing the same "check your inbox" copy on success.
  const [resendEmail, setResendEmail] = useState('');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let pollTimer;
    const start = Date.now();

    const poll = async () => {
      if (cancelled) return;
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data?.session?.user) {
        setEmail(data.session.user.email || '');
        setPhase('ready');
        return;
      }
      if (Date.now() - start > SESSION_WAIT_MS) {
        setPhase('expired');
        return;
      }
      pollTimer = setTimeout(poll, 250);
    };
    poll();

    // The SDK also fires SIGNED_IN once it finishes processing the URL hash;
    // this is usually faster than the next poll tick.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
        setEmail(session.user.email || '');
        setPhase('ready');
      }
    });

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  // Phase 84.4 — Preview the workspace invite (public endpoint, no auth
  // required) so we can show "You've been invited by <inviter>" copy
  // while the Supabase session finishes loading. This is a purely
  // decorative call; if it fails, the flow still works.
  //
  // Phase 84.20.8 — Also fetches the waitlist row (via the same public
  // preview endpoint if it returns names, else a follow-up call) to
  // pre-fill first_name / last_name inputs. Reduces friction for users
  // who already gave us their name at waitlist time.
  useEffect(() => {
    if (!email && !wsToken) return;
    let cancelled = false;
    (async () => {
      // Attempt 1: waitlist preview for the current email. Public
      // endpoint that returns first/last name if the caller matches.
      if (email) {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/waitlist/name-preview?email=${encodeURIComponent(email)}`,
          );
          if (res.ok) {
            const body = await res.json();
            if (!cancelled) {
              if (body?.first_name && !firstName) setFirstName(String(body.first_name));
              if (body?.last_name  && !lastName)  setLastName(String(body.last_name));
            }
          }
        } catch { /* decorative — safe to ignore */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Phase 84.4 — Preview the workspace invite (public endpoint, no auth
  // required) so we can show "You've been invited by <inviter>" copy
  // while the Supabase session finishes loading. This is a purely
  // decorative call; if it fails, the flow still works.
  useEffect(() => {
    if (!wsToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/workspaces/invite/${encodeURIComponent(wsToken)}/preview`,
        );
        if (!res.ok) return;
        const body = await res.json();
        if (!cancelled && body?.inviter_name) {
          setInviterName(body.inviter_name);
        }
      } catch { /* decorative — safe to ignore */ }
    })();
    return () => { cancelled = true; };
  }, [wsToken]);

  // Phase 55 follow-up — kick off a fresh invite. Calls the public
  // /api/auth/resend-invite endpoint; the backend silently no-ops for
  // unknown / unapproved emails, but always returns 200, so we present
  // the same success state regardless. Mild client-side email validation
  // mirrors the server's 400 trigger.
  const resendInvite = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const value = (resendEmail || '').trim();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setResendError('Please enter a valid email address.');
      return;
    }
    setResendError('');
    setResendBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value }),
      });
      // The server returns 200 with a generic ok for both eligible and
      // ineligible emails. Any 4xx/5xx is shown as a soft inline error
      // (the user can retry without losing context).
      if (!res.ok) {
        let detail = 'Something went wrong. Please try again, or write to accounts@2120.money.';
        try {
          const body = await res.json();
          if (body?.detail) detail = String(body.detail);
        } catch { /* ignore */ }
        setResendError(detail);
        return;
      }
      setResendSent(true);
    } catch {
      setResendError("We couldn't reach the server. Please check your connection and try again.");
    } finally {
      setResendBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Phase 84.20.8 — First & last name are now mandatory. We enforce
    // client-side here AND pass them to /api/auth/welcome so the backend
    // updates profiles.first_name / profiles.last_name atomically.
    const fn = (firstName || '').trim();
    const ln = (lastName  || '').trim();
    if (!fn) {
      toast.error('Please enter your first name.');
      return;
    }
    if (!ln) {
      toast.error('Please enter your last name.');
      return;
    }
    if (password.length < 8) {
      toast.error('Please choose a password at least 8 characters long.');
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match. Please retype to confirm.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      // Call /api/auth/welcome, backfills names from the waitlist row
      // into profiles and queues the delayed expectations email. As of
      // Phase 84.20.8 we also pass first_name + last_name explicitly so
      // the profile is populated even when the waitlist row was blank.
      let accessToken = null;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        accessToken = session?.access_token || null;
        if (accessToken) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/welcome`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ first_name: fn, last_name: ln }),
          });
        }
      } catch (_) { /* never block invite acceptance on a welcome-email hiccup */ }

      // Phase 84.4 — Workspace-share fast-path. If we arrived here from a
      // family invite (ws_token in URL), redeem the workspace invite now
      // and land the user on /dashboard, so they see their partner's
      // shared workspace immediately instead of running through the
      // solo onboarding flow.
      if (wsToken && accessToken) {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/workspaces/accept`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ token: wsToken }),
            },
          );
          if (res.ok) {
            toast.success(
              inviterName
                ? `Welcome in — you're now sharing ${inviterName.split(' ')[0]}'s workspace.`
                : "Welcome in — you've joined your partner's workspace.",
            );
            nav('/dashboard', { replace: true });
            return;
          }
          // If accept fails, we still let the user in via the standard
          // onboarding path — they can paste the 6-char code from
          // /invite manually. Surface a soft note so they know.
          let detail = '';
          try {
            const body = await res.json();
            detail = body?.detail || '';
          } catch { /* ignore */ }
          toast.warning(
            detail
              ? `Signed in, but we couldn't finish joining the shared workspace: ${detail}`
              : "Signed in, but we couldn't finish joining the shared workspace. You can paste your invite code from Settings.",
            { duration: 8000 },
          );
        } catch (err) {
          toast.warning(
            "Signed in, but we couldn't reach the workspace service. You can paste your invite code from Settings.",
            { duration: 7000 },
          );
        }
      }

      toast.success("Welcome to 2120. Let's set things up.");
      nav('/onboarding', { replace: true });
    } catch (err) {
      const msg = err?.message || 'Could not set your password.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground grid md:grid-cols-2">
      {/* Left hero panel, same visual rhythm as Signup / Login */}
      <div className="hidden md:flex bg-hero-soft items-center justify-center p-12">
        <div className="max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-mark.png" alt="2120" className="h-10 w-10" />
            <img src="/brand/wordmark-green.png" alt="2120" className="h-7 w-auto dark:hidden" />
            <img src="/brand/wordmark-gold.png" alt="2120" className="h-7 w-auto hidden dark:block" />
          </div>
          <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-card border border-border/60 text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" /> Private beta · by invitation
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight">
            Welcome in.
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            One short step, pick a password, and you're inside 2120. We'll match this email to your waitlist entry and seed your account so the next screen feels like home.
          </p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> Private + secure by default</li>
            <li className="flex items-center gap-2"><Lock className="h-4 w-4 text-primary" /> Last 4 digits only, never full card numbers</li>
            <li className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> No card on file. Ever, during beta.</li>
          </ul>
        </div>
      </div>

      {/* Right card */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md rounded-2xl border-border/70">
          <CardContent className="p-6 sm:p-8 space-y-5" data-testid="accept-invite-card">
            <div className="md:hidden flex flex-col items-center gap-2 pb-2 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <img src="/brand/logo-mark.png" alt="2120" className="h-9 w-9" />
                <img src="/brand/wordmark-green.png" alt="2120" className="h-6 w-auto dark:hidden" />
                <img src="/brand/wordmark-gold.png" alt="2120" className="h-6 w-auto hidden dark:block" />
              </div>
              <div className="text-xs text-muted-foreground text-center">Financial Clarity For Modern Life.</div>
            </div>

            {phase === 'pending' && (
              <div className="py-10 flex flex-col items-center justify-center gap-3 text-muted-foreground text-sm" data-testid="accept-invite-pending">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                Verifying your invitation…
              </div>
            )}

            {phase === 'expired' && (
              <div className="space-y-4" data-testid="accept-invite-expired">
                <div>
                  <div className="font-display text-2xl font-semibold">Invitation expired</div>
                  <div className="text-sm text-muted-foreground mt-1">This invite link has either expired or been used already.</div>
                </div>
                <div className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3.5 text-sm leading-relaxed text-foreground/85 flex items-start gap-2.5">
                  <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--gold))]" />
                  <div>
                    Invitations are one-time and expire after 24 hours. Enter the email you signed up with and we&rsquo;ll send a fresh link.
                  </div>
                </div>

                {resendSent ? (
                  <div
                    className="rounded-2xl border border-[hsl(var(--chart-5))]/40 bg-[hsl(var(--chart-5))]/8 px-4 py-3.5 text-sm leading-relaxed text-foreground flex items-start gap-2.5"
                    data-testid="accept-invite-resend-success"
                  >
                    <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--chart-5))]" />
                    <div>
                      Sent. Check your inbox in a few minutes (and spam, just in case). The new link will expire in 24 hours, so open it when you&rsquo;re ready to set your password.
                    </div>
                  </div>
                ) : (
                  <form onSubmit={resendInvite} className="space-y-3" data-testid="accept-invite-resend-form">
                    <div className="space-y-1.5">
                      <Label htmlFor="resend-email" className="text-xs">Email</Label>
                      <Input
                        id="resend-email"
                        type="email"
                        autoComplete="email"
                        value={resendEmail}
                        onChange={(e) => { setResendEmail(e.target.value); if (resendError) setResendError(''); }}
                        placeholder="you@example.com"
                        className="rounded-xl"
                        disabled={resendBusy}
                        data-testid="accept-invite-resend-email"
                      />
                      {resendError && (
                        <div className="text-xs text-destructive" data-testid="accept-invite-resend-error">{resendError}</div>
                      )}
                    </div>
                    <Button
                      type="submit"
                      disabled={resendBusy}
                      className="w-full rounded-xl"
                      data-testid="accept-invite-resend-submit"
                    >
                      {resendBusy ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending&hellip;</>
                      ) : (
                        <>Send me a new link</>
                      )}
                    </Button>
                  </form>
                )}

                <div className="text-sm text-muted-foreground">
                  Already have an account? <Link to="/login" className="text-primary hover:underline" data-testid="accept-invite-go-login">Sign in</Link>
                </div>
                <div className="text-xs text-muted-foreground">
                  Need help? Write to <a href="mailto:accounts@2120.money" className="text-primary underline underline-offset-2 hover:no-underline">accounts@2120.money</a>.
                </div>
              </div>
            )}

            {phase === 'ready' && (
              <>
                <div>
                  <div className="font-display text-2xl font-semibold">Set your password</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {email
                      ? <>You're signing in as <span className="text-foreground font-medium">{email}</span>.</>
                      : 'Pick a password to finish setup.'}
                  </div>
                </div>

                {/* Phase 84.4 — Family-invite badge. When we recognise a
                    workspace-share invite, replace the generic "private
                    beta access" copy with a warmer inviter-scoped message.
                    Falls back to the beta banner otherwise. */}
                {wsToken ? (
                  <div
                    className="rounded-2xl border border-primary/30 bg-primary/8 px-4 py-3.5 text-xs leading-relaxed text-foreground/85"
                    data-testid="accept-invite-family-banner"
                  >
                    <div className="flex items-start gap-2.5">
                      <Users2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                      <div>
                        <div className="font-medium text-foreground mb-0.5">
                          {inviterName
                            ? <>You&rsquo;re joining {inviterName}&rsquo;s shared workspace.</>
                            : <>You&rsquo;re joining a shared workspace.</>}
                        </div>
                        <div className="text-muted-foreground">
                          Pick a password and we&rsquo;ll drop you straight into the shared dashboard &mdash; no waitlist, no extra steps.
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="rounded-2xl border border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/8 px-4 py-3.5 text-xs leading-relaxed text-foreground/85"
                    data-testid="accept-invite-beta-banner"
                  >
                    <div className="flex items-start gap-2.5">
                      <Lock className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--gold))]" />
                      <div>
                        <div className="font-medium text-foreground mb-0.5">Private beta access.</div>
                        <div className="text-muted-foreground">
                          Your account is being created with full beta access. No card required.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={submit} className="space-y-4">
                  {/* Phase 84.20.8 — Name is now mandatory at invite
                      accept. Two side-by-side inputs pre-filled from the
                      waitlist row when available. */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="first-name">First name</Label>
                      <Input
                        id="first-name"
                        type="text"
                        autoComplete="given-name"
                        required
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        data-testid="accept-invite-first-name-input"
                        className="rounded-xl"
                        placeholder="e.g. Alex"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="last-name">Last name</Label>
                      <Input
                        id="last-name"
                        type="text"
                        autoComplete="family-name"
                        required
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        data-testid="accept-invite-last-name-input"
                        className="rounded-xl"
                        placeholder="e.g. Varghese"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password">New password</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="accept-invite-password-input"
                      className="rounded-xl"
                      placeholder="At least 8 characters"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirm">Confirm password</Label>
                    <Input
                      id="confirm"
                      type="password"
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      data-testid="accept-invite-confirm-input"
                      className="rounded-xl"
                      placeholder="Re-enter the same password"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full rounded-xl"
                    disabled={busy}
                    data-testid="accept-invite-submit-button"
                  >
                    {busy ? 'Setting up…' : 'Set password & enter 2120'}
                  </Button>
                </form>

                <div className="text-[11px] text-muted-foreground text-center">
                  <Sparkles className="h-3 w-3 inline-block mr-1 text-[hsl(var(--gold))]" />
                  Welcome to the private beta. Your feedback shapes what we ship.
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
